import apiClient from "./ApiClient";

/**
 * Create a new user subscription.
 * @param {Object} payload - User subscription data
 * @returns {Promise<Object>} created user subscription
 */
export function createUserSubscription(payload) {
  return apiClient
    .post("/user-subscriptions", payload)
    .then((res) => res.data);
}

/**
 * Get user subscriptions by restaurant ID.
 * @param {number|string} restaurantId
 * @returns {Promise<Object[]>} user subscriptions for the restaurant
 */
export function getUserSubscriptionsByRestaurantId(restaurantId) {
  return apiClient
    .get(`/user-subscriptions/restaurant/${restaurantId}`)
    .then((res) => res.data);
}

/**
 * Get completed user subscriptions by restaurant ID.
 * @param {number|string} restaurantId
 * @param {number} page - Page number (default: 0)
 * @param {number} size - Page size (default: 10)
 * @returns {Promise<Object>} paginated completed user subscriptions for the restaurant
 * @property {Object[]} content - Array of subscriptions
 * @property {number} totalElements - Total number of subscriptions
 * @property {number} totalPages - Total number of pages
 * @property {number} size - Page size
 * @property {number} number - Current page number
 */
export function getCompletedUserSubscriptionsByRestaurantId(restaurantId, page = 0, size = 10) {
  return apiClient
    .get(`/user-subscriptions/completed/restaurant/${restaurantId}?page=${page}&size=${size}`)
    .then((res) => res.data);
}

/**
 * Get active user subscriptions by restaurant ID.
 * @param {number|string} restaurantId
 * @param {number} page - Page number (default: 0)
 * @param {number} size - Page size (default: 10)
 * @returns {Promise<Object>} paginated active user subscriptions for the restaurant
 * @property {Object[]} content - Array of subscriptions
 * @property {number} totalElements - Total number of subscriptions
 * @property {number} totalPages - Total number of pages
 * @property {number} size - Page size
 * @property {number} number - Current page number
 */
export function getActiveUserSubscriptionsByRestaurantId(restaurantId, page = 0, size = 10) {
  return apiClient
    .get(`/user-subscriptions/active/restaurant/${restaurantId}?page=${page}&size=${size}`)
    .then((res) => res.data);
}

/**
 * Process checkout for user subscription.
 * @param {Object} checkoutRequest - Checkout data
 * @returns {Promise<Object>} checkout response
 */
export function checkout(checkoutRequest) {
  return apiClient
    .post("/user-subscriptions/checkout", checkoutRequest)
    .then((res) => res.data);
}

/**
 * Get active user subscriptions by user ID.
 * @param {number|string} userId
 * @returns {Promise<Object[]>} active user subscriptions
 */
export function getActiveUserSubscriptionsByUserId(userId) {
  return apiClient
    .get(`/user-subscriptions/active/user/${userId}`)
    .then((res) => res.data);
}

/**
 * Get completed user subscriptions by user ID.
 * @param {number|string} userId
 * @returns {Promise<Object[]>} completed user subscriptions
 */
export function getCompletedUserSubscriptionsByUserId(userId) {
  return apiClient
    .get(`/user-subscriptions/completed/user/${userId}`)
    .then((res) => res.data);
}

/**
 * Get user subscription by ID.
 * @param {number|string} id
 * @returns {Promise<Object>} user subscription
 */
export function getUserSubscriptionById(id) {
  return apiClient
    .get(`/user-subscriptions/${id}`)
    .then((res) => res.data);
}

/**
 * Get all user subscriptions by user ID.
 * @param {number|string} userId
 * @returns {Promise<Object[]>} user subscriptions
 */
export function getUserSubscriptionsByUserId(userId) {
  return apiClient
    .get(`/user-subscriptions/user/${userId}`)
    .then((res) => res.data);
}

/**
 * Get latest user subscription by user ID and payment status.
 * @param {number|string} userId
 * @param {string} paymentStatus - Payment status (e.g., 'PENDING', 'COMPLETED', 'FAILED')
 * @returns {Promise<Object>} latest user subscription
 */
export function getLatestUserSubscriptionByUserIdAndPaymentStatus(userId, paymentStatus) {
  return apiClient
    .get(`/user-subscriptions/user/${userId}/latest/${paymentStatus}`)
    .then((res) => res.data);
}

/**
 * Get user subscription statistics by restaurant ID.
 * @param {number|string} restaurantId
 * @returns {Promise<Object>} subscription statistics
 * @property {number} totalActiveSubscriptions - Total active subscriptions count
 * @property {number} totalCompletedSubscriptions - Total completed subscriptions count
 * @property {Object} activeUserSubscriptionIdsPerSubscription - Mapping of subscription IDs to active count
 */
export function getUserSubscriptionStatsByRestaurantId(restaurantId) {
  return apiClient
    .get(`/user-subscriptions/stats/restaurant/${restaurantId}`)
    .then((res) => res.data);
}

/**
 * Get user subscriptions by phone or email.
 * @param {string} phoneOrEmail - Phone number or email address
 * @returns {Promise<Object[]>} user subscriptions for the phone/email
 */
export function getUserSubscriptionsByPhoneOrEmail(phoneOrEmail) {
  return apiClient
    .get(`/user-subscriptions/search/user/${encodeURIComponent(phoneOrEmail)}`)
    .then((res) => res.data);
}

/**
 * Get customers by restaurant ID.
 * @param {number|string} restaurantId
 * @param {number} page - Page number (default: 0)
 * @param {number} size - Page size (default: 10)
 * @returns {Promise<Object>} paginated customers for the restaurant
 * @property {Object[]} content - Array of customer summaries
 * @property {number} totalElements - Total number of customers
 * @property {number} totalPages - Total number of pages
 * @property {number} size - Page size
 * @property {number} number - Current page number
 */
export function getCustomersByRestaurantId(restaurantId, page = 0, size = 10) {
  return apiClient
    .get(`/user-subscriptions/customers/restaurant/${restaurantId}?page=${page}&size=${size}`)
    .then((res) => res.data);
}